import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { organizationApi } from '@/lib/pillarApi';
import { getOrganizationCapacityLabel } from '@/lib/organization-capacity';
import type {
    LegalDocumentAcceptancesSearchRequest,
    OrganizationOmnisearchListItem,
} from '@pillar-portal/api-types';

export interface LegalDocumentAcceptanceFilters {
    readonly organization: { readonly organization_uuid: string; readonly name: string } | null;
    readonly acceptedText: 'current' | 'earlier' | null;
}

/** The request fields for the chosen filters; unset filters are left out. */
export function legalDocumentAcceptanceFilterRequest(
    filters: LegalDocumentAcceptanceFilters
): Pick<LegalDocumentAcceptancesSearchRequest, 'organization_uuid' | 'accepted_text'> {
    return {
        ...(filters.organization === null
            ? {}
            : { organization_uuid: filters.organization.organization_uuid }),
        ...(filters.acceptedText === null ? {} : { accepted_text: filters.acceptedText }),
    };
}

const acceptedTextOptions = [
    { value: '', label: 'Any text' },
    { value: 'current', label: 'Current text' },
    { value: 'earlier', label: 'An earlier text' },
] as const;

function parseAcceptedText(value: string): LegalDocumentAcceptanceFilters['acceptedText'] {
    return value === 'current' || value === 'earlier' ? value : null;
}

/** Filters for a legal document's acceptances or signatures. */
export function LegalDocumentAcceptanceFilterControls({
    filters,
    onChange,
}: {
    readonly filters: LegalDocumentAcceptanceFilters;
    readonly onChange: (filters: LegalDocumentAcceptanceFilters) => void;
}) {
    // The Organization search loads only once asked for, so opening a document
    // costs no Organization request.
    const [isOrganizationFilterOpen, setIsOrganizationFilterOpen] = useState(
        filters.organization !== null
    );
    return (
        <div className="grid gap-3 text-sm md:grid-cols-2">
            <label className="flex flex-col gap-1">
                <span>Accepted text</span>
                <select
                    className="rounded border px-2 py-1"
                    value={filters.acceptedText ?? ''}
                    onChange={(event) =>
                        onChange({
                            ...filters,
                            acceptedText: parseAcceptedText(event.target.value),
                        })
                    }
                >
                    {acceptedTextOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </label>
            {isOrganizationFilterOpen ? (
                <OmnisearchSelect<OrganizationOmnisearchListItem, string>
                    inputId="legal-acceptances-organization"
                    label="Organization"
                    placeholder="Search Organizations by name..."
                    pageSize={25}
                    selectedItemId={filters.organization?.organization_uuid ?? null}
                    selectedItemLabel={filters.organization?.name}
                    onClearSelection={() => {
                        onChange({ ...filters, organization: null });
                        setIsOrganizationFilterOpen(false);
                    }}
                    fetchPage={async (params) => {
                        const response = await organizationApi.searchOrganizations({
                            ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                            limit: params.limit,
                            cursor: params.cursor,
                        });
                        return { items: response.organizations, pagination: response.pagination };
                    }}
                    getItemId={(item) => item.organization_uuid}
                    getItemLabel={(item) => `${item.name} (${getOrganizationCapacityLabel(item)})`}
                    getItemFilterText={(item) => `${item.name} ${item.organization_uuid}`}
                    onSelect={(item) =>
                        onChange({
                            ...filters,
                            organization: {
                                organization_uuid: item.organization_uuid,
                                name: item.name,
                            },
                        })
                    }
                />
            ) : (
                <div className="flex items-end">
                    <Button variant="outline" onClick={() => setIsOrganizationFilterOpen(true)}>
                        Filter by Organization
                    </Button>
                </div>
            )}
        </div>
    );
}
